import React, { useEffect } from 'react';
import useReactRouter from 'use-react-router';
import { setStep, Step } from 'actions/step.action';
import Loading from 'components/loading';
import { RouteParams } from 'components/custom-route/custom-route';
import { LoanApplicationStatus, LoanApplication, useGetLoanLazyQuery } from 'generated/graphql';
import useNavigation from 'hooks/navigation';
import { proposalSchema, addressSchema, bankAccountSchema } from 'validation-schemas/balcao';
import { useProfile } from 'providers/profile.provider';
import { useProduct } from 'providers/product.provider';
import { productConfig } from 'constants/constants';
import { getLoanApplicationParams, getMaxTenure } from 'utils/utils';

const LoadProposal: React.FC = () => {
    const { match, history } = useReactRouter<RouteParams>();
    const loanApplicationId = getLoanApplicationParams();

    const [loanApplicationQuery] = useGetLoanLazyQuery({
        fetchPolicy: "network-only"
    });

    const {
        setProductDataFillOutFlow,
        setAllowedBanks,
        setRequiredDocumentation,
        setProductBizId,
        currentScreen,
        setCurrentScreen,
        setMaxNumberOfInstallments,
        setMinNumberOfInstallments
    } = useProduct();

    const { setLoanApplicationUtmSource, setLoanApplicationUtmMedium } = useProfile();
    const {
        goToLoanConfiguration,
        goToAddressConfiguration,
        goToBankAccountConfiguration,
        goToProposalConfirmation,
        goToSummary,
        goToError,
        goToBndes,
        goToProposalExpired,
        goToLoanDenied,
        goToLoanCancelled,
        goToAntiFraudCheckout,
        goToDocumentCheckout,
        goToChooseBankAccount,
        goToApproved,
        buildUrl
    } = useNavigation();

    useEffect(() => {
        const loadLoanApplication = async () => {
            await loanApplicationQuery({
                variables: {
                    id: loanApplicationId
                }
            }).then(({ data: { loanApplication } }) => (
                loanApplication
                || Promise.reject('LoanApplication Not Found.'))
            )
                .then((loanApplication: LoanApplication) => {
                    if (loanApplication) {
                        const trakers: any = loanApplication.tracking && JSON.parse(loanApplication.tracking.trackers);
                        const historySearch: string[] = [];

                        if (trakers && trakers.utm_source) {
                            historySearch.push(`utm_source=${trakers.utm_source}`);
                            setLoanApplicationUtmSource(trakers.utm_source);
                        }

                        if (trakers && trakers.utm_medium) {
                            historySearch.push(`utm_medium=${trakers.utm_medium}`);
                            setLoanApplicationUtmMedium(trakers.utm_medium);
                        }

                        if (historySearch.length) {
                            history.push({ search: historySearch.join('&') });
                        }
                    }

                    const antifraudStepStatuses = [
                        LoanApplicationStatus.Awaiting,
                        LoanApplicationStatus.Lost,
                    ];

                    if (loanApplication.product) {
                        setProductBizId(loanApplication.product.bizId);
                        setMaxNumberOfInstallments(loanApplication.product.maxNumberOfInstallments);
                        setMinNumberOfInstallments(loanApplication.product.minNumberOfInstallments);
                    }

                    const { LOAN_AMOUNT, TENURE } = productConfig(loanApplication.product.bizId, loanApplication.product.maxNumberOfInstallments, loanApplication.product.minNumberOfInstallments);
                    const maxTenure = getMaxTenure(loanApplication, TENURE.MAX());

                    if (loanApplication.hasAntiFraudWorkflow
                        && proposalSchema(
                            LOAN_AMOUNT.MIN(),
                            LOAN_AMOUNT.MAX(),
                            TENURE.MIN(),
                            maxTenure
                        ).isValidSync(loanApplication.acceptedProposal)
                        && antifraudStepStatuses.includes(loanApplication.status)) {

                        goToAntiFraudCheckout();
                        return;
                    }

                    const documentationStepStatuses = [
                        LoanApplicationStatus.InAnalysis,
                        LoanApplicationStatus.DocumentsPending,
                        LoanApplicationStatus.Lost,
                    ];

                    if (loanApplication.hasDocumentationUploadWorkflow
                        && proposalSchema(
                            LOAN_AMOUNT.MIN(),
                            LOAN_AMOUNT.MAX(),
                            TENURE.MIN(),
                            maxTenure)
                            .isValidSync(loanApplication.acceptedProposal)
                        && documentationStepStatuses.includes(loanApplication.status)) {

                        if (loanApplication.status === LoanApplicationStatus.InAnalysis && loanApplication.acceptedBizOsAccount === null) {
                            goToChooseBankAccount();
                        } else {
                            goToDocumentCheckout();
                        }

                        return;
                    }

                    if (loanApplication.status === LoanApplicationStatus.Approved) {
                        goToApproved();
                        return;
                    }

                    if (loanApplication.product && loanApplication.product.dataFillOutFlow) {
                        setAllowedBanks(loanApplication.product.allowedBanks);
                        setRequiredDocumentation(loanApplication.product.requiredDocumentation);
                        setProductDataFillOutFlow(loanApplication.product.dataFillOutFlow.map((data) => {
                            return { path: data.routerPath };
                        }));
                        setProductBizId(loanApplication.product.bizId);

                        if (loanApplication.product.dataFillOutFlow.length > 0 && !currentScreen) {
                            setCurrentScreen(loanApplication.product.dataFillOutFlow[0].routerPath)
                            history.push(buildUrl(loanApplication.product.dataFillOutFlow[0].routerPath, loanApplication.id))
                        } else if (currentScreen) {
                            history.push(buildUrl(currentScreen, loanApplication.id))
                        }
                    } else if (loanApplication.status === LoanApplicationStatus.Draft) {
                        history.replace(`/${window.location.search}`);
                    } else if (!loanApplication || loanApplication.status === LoanApplicationStatus.Received) {
                        setStep(Step.FLOW_FINISHED);
                        goToError();
                    } else if (loanApplication.status === LoanApplicationStatus.Expired) {
                        setStep(Step.FLOW_FINISHED);
                        goToProposalExpired();
                    } else if (loanApplication.status === LoanApplicationStatus.Denied) {
                        setStep(Step.FLOW_FINISHED);
                        goToLoanDenied();
                    } else if (loanApplication.status === LoanApplicationStatus.Rejected) {
                        setStep(Step.FLOW_FINISHED);
                        goToLoanCancelled();
                    } else if ([LoanApplicationStatus.Awaiting, LoanApplicationStatus.Lost].includes(loanApplication.status)) {
                        proposalConfigurationWorkflow(loanApplication);
                    } else {
                        goToSummary();
                    }
                }).catch(() => {
                    if (match.params.loanApplicationId !== 'bndes') {
                        setStep(Step.FLOW_FINISHED);
                        goToError();
                    } else {
                        goToBndes();
                    }
                });
        }

        loadLoanApplication();
    }, []);

    async function proposalConfigurationWorkflow(loanApplication: LoanApplication) {
        const { LOAN_AMOUNT, TENURE } = productConfig(loanApplication.product.bizId, loanApplication.product.maxNumberOfInstallments, loanApplication.product.minNumberOfInstallments);

        const maxTenure = getMaxTenure(loanApplication, TENURE.MAX());

        if (!proposalSchema(
            LOAN_AMOUNT.MIN(),
            LOAN_AMOUNT.MAX(),
            TENURE.MIN(),
            maxTenure
        ).isValidSync(loanApplication.acceptedProposal)) {
            goToLoanConfiguration();
        } else if (!addressSchema.isValidSync(loanApplication.borrower.address)) {
            goToAddressConfiguration();
        } else if (!bankAccountSchema.isValidSync(loanApplication.borrower.bankAccount)) {
            goToBankAccountConfiguration();
        } else {
            goToProposalConfirmation();
        }
    }

    return (
        <Loading />
    );
}

export default LoadProposal;

